// Dialog basado en el elemento HTML <dialog> nativo. Accesible (Esc cierra,
// focus trap automático), sin deps de Radix.
//
// API mínima:
//   <Dialog open={open} onClose={() => setOpen(false)}>
//     <DialogHeader>
//       <DialogTitle>Título</DialogTitle>
//     </DialogHeader>
//     <DialogContent>...</DialogContent>
//     <DialogFooter>...</DialogFooter>
//   </Dialog>

import { useEffect, useRef, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../lib/utils';

interface DialogProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  /** Si true, NO se puede cerrar con Esc ni click fuera (caso modal forzado). */
  forceConfirm?: boolean;
  className?: string;
}

export function Dialog({ open, onClose, children, forceConfirm = false, className }: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const handleClose = () => {
      if (open) onClose();
    };
    const handleCancel = (e: Event) => {
      if (forceConfirm) e.preventDefault();
    };
    el.addEventListener('close', handleClose);
    el.addEventListener('cancel', handleCancel);
    return () => {
      el.removeEventListener('close', handleClose);
      el.removeEventListener('cancel', handleCancel);
    };
  }, [open, onClose, forceConfirm]);

  // Backdrop click cierra (a menos que forceConfirm). Solo cerramos si el
  // click fue DIRECTAMENTE en el <dialog> (que ocupa todo el viewport con
  // ::backdrop) — NO si fue en un descendiente (select/option/input/button).
  //
  // Bug previo: comparábamos coords del click contra `e.target.getBoundingClientRect()`,
  // pero `e.target` es el descendiente real (ej. <option>) cuyo rect es chico
  // → cualquier click en un option daba `inDialog=false` y cerraba el modal
  // al elegir una opción. `e.currentTarget` es el <dialog> al que está atado
  // el handler, así que `e.target === e.currentTarget` solo es true cuando
  // el click fue directo en el dialog/backdrop.
  function handleBackdropClick(e: React.MouseEvent<HTMLDialogElement>) {
    if (forceConfirm) return;
    if (e.target === e.currentTarget) onClose();
  }

  return (
    <dialog
      ref={ref}
      onClick={handleBackdropClick}
      className={cn(
        // Centrado explícito: el margin:auto del <dialog> nativo no es confiable
        // (en este app quedaba pegado arriba-izquierda). fixed + translate centra
        // siempre. max-h + overflow para que el contenido alto scrollee adentro.
        'fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
        'max-h-[90vh] w-[calc(100vw-2rem)] max-w-md overflow-y-auto',
        'rounded border border-border bg-surface-elevated text-fg p-0 shadow-elevated',
        'backdrop:bg-black/50',
        'open:animate-in open:fade-in-0 open:zoom-in-95',
        className,
      )}
    >
      {children}
    </dialog>
  );
}

export function DialogHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('flex flex-col gap-1.5 px-6 pt-6', className)} {...props} />;
}

export function DialogTitle({ className, ...props }: HTMLAttributes<HTMLHeadingElement>) {
  return <h2 className={cn('text-lg font-medium text-fg', className)} {...props} />;
}

export function DialogDescription({ className, ...props }: HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cn('text-sm text-fg-secondary', className)} {...props} />;
}

export function DialogContent({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('px-6 py-4', className)} {...props} />;
}

export function DialogFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('flex items-center justify-end gap-2 px-6 pb-6 pt-2', className)}
      {...props}
    />
  );
}
